import React, {
  ChangeEvent,
  SyntheticEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import URI from "urijs";
import { AppPage } from "../../components";
import { ActionType } from "../../@types";
import { getPDEInfo, useTranslate } from "../../helpers";
import { Routes } from "../../app";
import { AppContext } from "../../app/store";
import { useHistory, useLocation } from "react-router";
import { setPlanTypeFromURL } from "../../app/modules/location/actions";
import { Link } from "react-router-dom";
import {
  getSelectedPdes,
  PDEChoice,
  PDESelector,
} from "../../components/PDESelector";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";

interface PDEPageProps {
  isFromSummaryPage?: boolean;
}

const PDEPage: React.FunctionComponent<PDEPageProps> = ({
  isFromSummaryPage = false,
}) => {
  const { state, dispatch } = useContext(AppContext);
  const history = useHistory();
  const location = useLocation();
  const [choices, setChoices] = useState<PDEChoice[]>([]);
  const t = useTranslate();
  const isStandaloneDrugPage = state.mbpReferrerUri !== "" || isFromSummaryPage;
  const { savedPdes, unsavedPdes } = getPDEInfo(
    state.prescriptions,
    state.beneficiary?.pdes
  );

  useEffect(() => {
    if (!state.planType) {
      const url = new URI(location.search);
      setPlanTypeFromURL(dispatch, url);
    }

    if (!state.beneficiary) {
      history.replace(`${Routes.managePrescriptions}${location.search}`);
    }
  }, []);

  const onSelectAllPdes = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.currentTarget.checked) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Find Plans - LIS Select Drugs",
          event_label: "Select all drugs",
        },
      });
    }
  };

  const onSelectPde = (e: SyntheticEvent<HTMLInputElement>): void => {
    if (e.currentTarget.checked) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Find Plans - LIS Select Drugs",
          event_label: "Drug Selected",
        },
      });
    }
  };

  const sendItOut = (): void => {
    const selectedPdes = getSelectedPdes(unsavedPdes);

    dispatch({
      type: ActionType.SET_PRESCRIPTIONS,
      payload: [...state.prescriptions, ...selectedPdes],
    });

    const tealiumDrugList = selectedPdes.map(d => d.name);
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Find Plans - LIS Confirm Drugs",
        event_label: tealiumDrugList.join("|"),
      },
    });
  };

  const managePrescriptionsLink = isFromSummaryPage
    ? `${Routes.summary.managePrescriptions}${location.search}`
    : `${Routes.managePrescriptions}${location.search}`;

  const prescriptionsListLink = isFromSummaryPage
    ? `${Routes.summary.managePrescriptions}${location.search}`
    : `${Routes.prescriptionsList}${location.search}`;

  return (
    <AppPage heading={t("pde_page.add_recently_filled_drugs")}>
      <div className="PDEPage">
        <p className="ds-u-measure--wide ds-u-margin-bottom--3 ds-u-margin-top--0">
          {t("pde_page.pde_information")}
        </p>
        <PDESelector
          savedPdes={savedPdes}
          unsavedPdes={unsavedPdes}
          onChoiceUpdated={setChoices}
          onSelectAllPdes={onSelectAllPdes}
          onSelectPde={onSelectPde}
        />
        <section className="ContinueSection">
          <Link
            to={
              state.prescriptions.length ||
              choices.some(choice => choice.checked)
                ? prescriptionsListLink
                : managePrescriptionsLink
            }
            className="ds-c-button ds-c-button--solid e2e-continue-button mct-u-width--full mct-u-sm-width--auto"
            onClick={sendItOut}
            data-testid="pde-next-button"
          >
            {isStandaloneDrugPage
              ? t("manage_prescriptions.back_to_my_saved_drugs")
              : t("landing_page.continue")}
          </Link>
        </section>
      </div>
    </AppPage>
  );
};

export default PDEPage;
